// White Space tab shell — Story #4937 Stream 2b/2c. Fetches scored regions +
// matrix from Stream 1's backend, manages the scan lifecycle (regenerate →
// poll → refresh), and hosts the Opportunity Explorer / Combination Matrix
// sub-views the mockups show as an internal tab bar (screen2/screen3).
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Box, Compass, Map as MapIcon, RefreshCw, X } from 'lucide-react'
import {
  createWhitespaceWatch,
  deleteWhitespaceWatch,
  getWhitespace3D,
  getWhitespaceCompetitors,
  getWhitespaceMap,
  getWhitespaceMatrix,
  getWhitespaceRegions,
  getWhitespaceScan,
  listWhitespaceWatches,
  requestWhitespace3D,
  requestWhitespaceMap,
  requestWhitespaceScan,
} from '../../services/api'
import {
  corpusCoverageNote,
  firstRunCtaCopy,
  isTerminalScanStatus,
  nextPollDecision,
  planCollapsedWatchToggle,
  POLL_INTERVAL_MS,
  regionBandKey,
  regionCorpusLabel,
  regionLabel,
  regionWatchRecordIds,
  watchIdsByBand,
} from '../../lib/whitespace'
import { regionKeyFor } from '../../lib/whitespace-map'
import { DEFAULT_CHANNEL_ASSIGNMENT } from '../../lib/whitespace-3d'
import type { WhitespaceProbeLanding, WhitespaceRegion, WhitespaceScanStatus } from '../../types/whitespace'
import OpportunityExplorer from './OpportunityExplorer'
import OpportunityDetail from './OpportunityDetail'
import AnalysisPanel from './AnalysisPanel'
import CombinationMatrix from './CombinationMatrix'
import LandscapeMap from './LandscapeMap'
import ProbePanel from './ProbePanel'
import ScanStatusChip, { type ScanChipState } from './ScanStatusChip'
import LoadingSkeleton from '../shared/LoadingSkeleton'
import EmptyState from '../shared/EmptyState'
import HelpPopover from '../help/HelpPopover'
import GuidedTour, { hasSeenTour, type TourStep } from '../help/GuidedTour'
import { onTourRequest } from '../help/helpBus'
import { MAP_PROJECTION_HONESTY_NOTE } from '../../constants/helpContent'

// Story #5305 Task #5327 Stream 11c (design §5.8) — the project-side sibling
// of Stream 14d's "Reading your portfolio" tour, same pattern: each step's
// onBeforeStep switches WhiteSpaceTab's own `view` state BEFORE GuidedTour
// looks for that step's data-tour target, so the tour walks the real,
// currently-rendered sub-view rather than a scripted mockup.
export const READING_SCAN_TOUR_ID = 'reading-whitespace-scan'

// POST /whitespace/panel/{project_id} caps at 20 record ids (whitespace/
// panel.py's MAX_RECORD_IDS) — capped here too so the request the user sees
// in flight matches what the backend will actually use.
const MAX_PANEL_RECORD_IDS = 20

// Lazy-loaded ONLY when the view switches to '3d' — three.js must never
// enter the main/index bundle chunk (Explorer3D.tsx's own header note +
// vite.config.ts's manualChunks). Do NOT change this to a static import.
const Explorer3D = lazy(() => import('./Explorer3D'))

type WhiteSpaceView = 'explorer' | 'matrix' | 'map' | '3d'

interface WhiteSpaceTabProps {
  projectId: string
  /** Real handoff to the project's existing novelty/assessment tab (Story
   * #4937 Stream 4c) — there is no region-scoped probe route, so "running a
   * novelty probe" from White Space means taking the user to the same
   * AI-analysis flow every other novelty entry point in this app uses.
   * Optional so this component still renders standalone in tests. */
  onNavigateToNoveltyProbe?: () => void
}

/**
 * The message shown when a scan cannot start. The backend refuses to scan a
 * project with no documents (400 "Project has no documents to scan"); that is
 * not a failure of the scan, it is a precondition, and the screen says what to
 * do about it (White-Space UX review, 6 Sep 2026 — Polaris Bio).
 */
export function scanStartErrorMessage(err: unknown): string {
  const e = err as { response?: { status?: number; data?: { error?: string } }; message?: string } | undefined
  const body = e?.response?.data?.error ?? ''
  if (e?.response?.status === 400 && /no documents/i.test(body)) {
    return 'Add documents to this invention before running White Space Discovery — the scan reads the documents, and this invention has none yet.'
  }
  if (body) return body
  return e?.message ?? 'Failed to start scan.'
}

export default function WhiteSpaceTab({ projectId, onNavigateToNoveltyProbe }: WhiteSpaceTabProps) {
  const qc = useQueryClient()
  const [view, setView] = useState<WhiteSpaceView>('explorer')
  const [runId, setRunId] = useState<string | null>(null)
  const [scanStartedAt, setScanStartedAt] = useState<number | null>(null)
  const [scanError, setScanError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [selectedRegion, setSelectedRegion] = useState<WhitespaceRegion | null>(null)
  // The Opportunity Explorer's "Open detail" renders the detail card BELOW the
  // card grid, which can land below the fold on a tall two-card layout — so the
  // click reads as doing nothing (UX review Finding 2). Scroll the detail into
  // view whenever it opens so the response is always visible.
  const detailRef = useRef<HTMLDivElement>(null)
  const [panelRegion, setPanelRegion] = useState<WhitespaceRegion | null>(null)
  const [probeLandings, setProbeLandings] = useState<WhitespaceProbeLanding[]>([])
  const [tourOpen, setTourOpen] = useState(false)
  // design §5.4 — "Probe this region on the map" carries region context "by
  // prop/remount": clicking a region's probe affordance sets BOTH the
  // context text and bumps the nonce, and the nonce is used as ProbePanel's
  // React `key` so the panel actually remounts (a plain prop update would
  // leave any in-progress freetext/mode state from the LAST region bleeding
  // into this one).
  const [probeContext, setProbeContext] = useState('')
  const [probeContextNonce, setProbeContextNonce] = useState(0)
  const [isBuildingMap, setIsBuildingMap] = useState(false)
  const [mapError, setMapError] = useState<string | null>(null)
  const [isBuilding3D, setIsBuilding3D] = useState(false)
  const [map3DError, setMap3DError] = useState<string | null>(null)
  const [build3DStartedAt, setBuild3DStartedAt] = useState<number | null>(null)

  const {
    data: regionsData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['whitespace-regions', projectId],
    queryFn: () => getWhitespaceRegions(projectId),
    enabled: !!projectId,
  })

  const { data: mapData, isLoading: isMapLoading } = useQuery({
    queryKey: ['whitespace-map', projectId],
    queryFn: () => getWhitespaceMap(projectId),
    enabled: !!projectId,
  })

  // Story #5305 Task #5326 Stream 11b — the real v2 Combination Matrix
  // (whitespace/matrix.py), replacing the Task 2d documented-gap `null`.
  // Only fetched once the matrix view is actually open — the same
  // fetch-on-demand convention view==='3d' already uses for competitors,
  // so a project that never opens the tab never pays for the compute.
  const {
    data: matrixData,
    isLoading: isMatrixLoading,
    refetch: refetchMatrix,
  } = useQuery({
    queryKey: ['whitespace-matrix', projectId],
    queryFn: () => getWhitespaceMatrix(projectId),
    enabled: !!projectId && view === 'matrix',
  })

  const {
    data: map3DData,
    isLoading: isMap3DLoading,
    refetch: refetch3D,
  } = useQuery({
    queryKey: ['whitespace-3d', projectId],
    queryFn: () => getWhitespace3D(projectId),
    enabled: !!projectId,
  })

  // Story #5116 Stream 8e (Task #5156) — competitor-proximity regions feed
  // the 3-D Explorer's dimension rig attribute shelf. Only needed once the
  // 3-D tab is actually open; `enabled` still gates on projectId so this
  // never fires before a project is known, same convention as every other
  // query on this tab.
  const { data: competitorsData } = useQuery({
    queryKey: ['whitespace-competitors', projectId],
    queryFn: () => getWhitespaceCompetitors(projectId),
    enabled: !!projectId && view === '3d',
  })

  // Story #4937 Stream 4d — real watch state, not the "not yet available"
  // stub Stream 2 shipped before the backend CRUD (Task #4959) existed.
  const { data: watches } = useQuery({
    queryKey: ['whitespace-watches'],
    queryFn: listWhitespaceWatches,
    enabled: !!projectId,
  })
  const watchMap = watchIdsByBand(watches ?? [], projectId)
  const watchedBandKeys = new Set(Object.keys(watchMap))

  const watchMutation = useMutation({
    mutationFn: async (region: WhitespaceRegion) => {
      const bandKey = regionBandKey(region.band)
      const existingWatchId = watchMap[bandKey]
      if (existingWatchId) {
        await deleteWhitespaceWatch(existingWatchId)
        return { action: 'unwatched' as const }
      }
      await createWhitespaceWatch({
        project_id: projectId,
        corpus: regionCorpusLabel(region),
        band: region.band,
        record_ids: regionWatchRecordIds(region),
        wos_score: region.wos_score,
      })
      return { action: 'watched' as const }
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['whitespace-watches'] })
      setNotice(result.action === 'watched' ? 'Added to your watchlist.' : 'Removed from your watchlist.')
    },
    onError: (e) => {
      setNotice((e as Error)?.message ?? 'Failed to update watchlist.')
    },
  })

  const handleToggleWatch = (region: WhitespaceRegion) => {
    setNotice(null)
    watchMutation.mutate(region)
  }

  // design §5.1 — watching a collapsed empty-span card creates one watch
  // per constituent band, idempotent (planCollapsedWatchToggle only targets
  // bands not already watched) and all-or-rollback (if any create in THIS
  // batch fails, every create this batch already made is undone, leaving
  // whatever was watched before the click unchanged).
  const groupWatchMutation = useMutation({
    mutationFn: async (regions: WhitespaceRegion[]) => {
      const plan = planCollapsedWatchToggle(regions, watchMap)
      if (plan.action === 'unwatch') {
        const results = await Promise.allSettled(plan.toDelete.map((id) => deleteWhitespaceWatch(id)))
        const failed = results.filter((r) => r.status === 'rejected').length
        return { action: 'unwatched' as const, total: plan.toDelete.length, failed }
      }
      const created: string[] = []
      try {
        for (const region of plan.toCreate) {
          const watch = await createWhitespaceWatch({
            project_id: projectId,
            corpus: regionCorpusLabel(region),
            band: region.band,
            record_ids: regionWatchRecordIds(region),
            wos_score: region.wos_score,
          })
          created.push(watch.watch_id)
        }
      } catch (e) {
        // All-or-rollback: undo every watch THIS call created, leaving any
        // pre-existing watches (from before this click) untouched.
        await Promise.allSettled(created.map((id) => deleteWhitespaceWatch(id)))
        throw e
      }
      return { action: 'watched' as const, total: plan.toCreate.length, failed: 0 }
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['whitespace-watches'] })
      if (result.action === 'watched') {
        setNotice(
          result.total > 0
            ? `Watching ${result.total} band${result.total === 1 ? '' : 's'}.`
            : 'Already watching every band in this group.',
        )
      } else {
        setNotice(
          result.failed > 0
            ? `Removed ${result.total - result.failed} of ${result.total} watches — retry to remove the rest.`
            : 'Removed the whole group from your watchlist.',
        )
      }
    },
    onError: () => {
      setNotice('Failed to update the group watch — nothing was changed.')
    },
  })

  const handleToggleWatchGroup = (regions: WhitespaceRegion[]) => {
    setNotice(null)
    groupWatchMutation.mutate(regions)
  }

  const handleRunNoveltyProbe = () => {
    if (onNavigateToNoveltyProbe) {
      onNavigateToNoveltyProbe()
    } else {
      setNotice('Novelty probes run from this project’s AI Analysis tab.')
    }
  }

  // Bring the opportunity detail card into view when it opens (UX review
  // Finding 2 — "Open detail" rendered below the fold and read as unresponsive).
  useEffect(() => {
    if (selectedRegion) {
      // Optional-call: jsdom (test env) does not implement scrollIntoView.
      detailRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
    }
  }, [selectedRegion])

  const { data: scanResult } = useQuery({
    queryKey: ['whitespace-scan', runId],
    queryFn: () => getWhitespaceScan(runId as string),
    enabled: !!runId,
    refetchInterval: (query) => {
      const status = query.state.data?.status as WhitespaceScanStatus | undefined
      if (!status) return 2000
      const decision = nextPollDecision(status, Date.now() - (scanStartedAt ?? Date.now()))
      return decision.action === 'poll' ? (decision.delayMs as number) : false
    },
  })

  useEffect(() => {
    if (scanResult && isTerminalScanStatus(scanResult.status)) {
      qc.invalidateQueries({ queryKey: ['whitespace-regions', projectId] })
      if (scanResult.status === 'failed') setScanError(scanResult.error ?? 'Scan failed — please try again.')
      setRunId(null)
    }
  }, [scanResult, qc, projectId])

  // Task #5325 "silent-poll-timeout fix": refetchInterval below already
  // stops issuing new requests once nextPollDecision says 'stop' — but with
  // no NEW fetch ever arriving after that, `scanResult` never changes again,
  // so a fix living in the effect above (which only runs when scanResult
  // changes) would never fire either: polling stops, nothing ever notices.
  // This is a genuinely separate timer, independent of query data, that
  // checks on its own schedule — the one thing missing before this fix.
  const latestScanStatus = useRef<WhitespaceScanStatus | undefined>(undefined)
  latestScanStatus.current = scanResult?.status
  useEffect(() => {
    if (!runId || !scanStartedAt) return
    const interval = window.setInterval(() => {
      const status = latestScanStatus.current
      if (!status || isTerminalScanStatus(status)) return
      const decision = nextPollDecision(status, Date.now() - scanStartedAt)
      if (decision.action === 'stop') {
        setScanError('This scan is taking longer than expected. It may still finish in the background — try Regenerate again shortly, or check back later.')
        setRunId(null)
      }
    }, POLL_INTERVAL_MS)
    return () => window.clearInterval(interval)
  }, [runId, scanStartedAt])

  const isScanRunning = !!runId && !(scanResult && isTerminalScanStatus(scanResult.status))

  const handleRegenerate = async () => {
    setScanError(null)
    setNotice(null)
    try {
      const run = await requestWhitespaceScan(projectId)
      setRunId(run.run_id)
      setScanStartedAt(Date.now())
    } catch (e) {
      setScanError(scanStartErrorMessage(e))
    }
  }

  const handleGenerateMap = async () => {
    setMapError(null)
    setIsBuildingMap(true)
    try {
      await requestWhitespaceMap(projectId)
      qc.invalidateQueries({ queryKey: ['whitespace-map', projectId] })
    } catch (e) {
      setMapError((e as Error)?.message ?? 'Failed to build the map.')
    } finally {
      setIsBuildingMap(false)
    }
  }

  const handleGenerate3D = async () => {
    setMap3DError(null)
    setIsBuilding3D(true)
    setBuild3DStartedAt(Date.now())
    try {
      await requestWhitespace3D(projectId)
      qc.invalidateQueries({ queryKey: ['whitespace-3d', projectId] })
    } catch (e) {
      setMap3DError((e as Error)?.message ?? 'Failed to build the 3-D map.')
    } finally {
      setIsBuilding3D(false)
      setBuild3DStartedAt(null)
    }
  }

  // design §5.2: "The 3-D view starts building automatically the first time
  // the user opens it" — fires exactly once per mount, the moment the tab
  // switches to '3d' with no cached layout yet and nothing already in
  // flight. `autoBuild3DTried` (not just `isBuilding3D`) is what prevents a
  // retry loop on a genuine build failure — a failed auto-attempt leaves the
  // manual "Build 3-D map" button (still wired to the same handler) as the
  // fallback, same as before this task.
  const autoBuild3DTried = useRef(false)
  useEffect(() => {
    if (view !== '3d') return
    if (autoBuild3DTried.current) return
    if (isMap3DLoading || isBuilding3D) return
    if (map3DData?.layout && Object.keys(map3DData.layout).length > 0) return
    autoBuild3DTried.current = true
    handleGenerate3D()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire-once guard is autoBuild3DTried, not a dependency list; re-running on every isMap3DLoading/isBuilding3D tick is the bug this ref prevents.
  }, [view, isMap3DLoading, map3DData])

  const regions = regionsData?.regions ?? []
  const hasRegions = regions.length > 0
  // Task #5325 "stale-results-visible rescans" (design §5.2): a rescan
  // keeps whatever was already loaded on screen — only a scan with NO prior
  // result yet blocks the sub-view with the full "Scanning…" message.
  const isFirstScan = isScanRunning && !hasRegions
  const isRescanning = isScanRunning && hasRegions
  // Task #5326 Stream 11b (design §5.3): the real v2 matrix, fetched above
  // only once the matrix view is open. `null` while loading/never-fetched —
  // CombinationMatrix's own 'no_scan_yet' state renders identically to a
  // plain null, so a slow first fetch never flashes a different empty state
  // than a genuinely-empty result would.
  const matrix = matrixData ?? null
  const coverageNote = regionsData?.corpus_coverage_note ?? corpusCoverageNote(regions.length)

  // "Reading your white-space scan" guided tour (design §5.8) — steps:
  // Explorer verdict cards & ranking -> Combination Matrix -> Landscape Map
  // -> probing a direction. Each onBeforeStep switches this tab's own
  // `view` so the tour always walks the real, currently-scored data.
  const scanTourSteps: TourStep[] = useMemo(
    () => [
      {
        title: 'Reading your white-space scan',
        body: 'Your scan just finished — this quick walkthrough shows the four ways to read it: ranked opportunity cards, the combination matrix, the landscape map, and probing your own directions.',
        onBeforeStep: () => setView('explorer'),
      },
      {
        target: 'ws-rank-strip',
        title: 'Ranked by opportunity, not distance',
        body: 'Cards are ranked by opportunity score, not by how close a band is to your project — open “Why this score?” on any card for its real sub-score counts, or use the ? icons here for the distance bands and the ranking rule.',
        onBeforeStep: () => setView('explorer'),
      },
      {
        target: 'combination-matrix',
        title: 'The Combination Matrix',
        body: 'Crosses your idea’s features against real application domains — a dashed cell is combination white space: both halves exist elsewhere, the pairing between them doesn’t.',
        onBeforeStep: () => setView('matrix'),
      },
      {
        target: 'landscape-map',
        title: 'The Landscape Map',
        body: `The same scored regions, placed on a 2-D map. ${MAP_PROJECTION_HONESTY_NOTE}`,
        onBeforeStep: () => setView('map'),
      },
      {
        target: 'probe-panel',
        title: 'Probe your own direction',
        body: 'Describe a variation of your idea, or ask for AI-suggested directions — it is embedded and placed on the map so you can compare it against the ranked regions before running a full novelty probe.',
        onBeforeStep: () => setView('map'),
      },
    ],
    [],
  )

  // Auto-start once, the first time this project has a completed scan with
  // something to show (design §5.8: "triggered on the first completed scan,
  // when there is something to see") — never on an empty/still-scanning
  // project, and never more than once per user (GuidedTour's own
  // localStorage seen-flag, checked here since the tour is host-controlled).
  useEffect(() => {
    if (!hasRegions) return
    if (hasSeenTour(READING_SCAN_TOUR_ID)) return
    setTourOpen(true)
  }, [hasRegions])

  // Replay requested from the Resources panel's tour launcher.
  useEffect(
    () =>
      onTourRequest((tourId) => {
        if (tourId === READING_SCAN_TOUR_ID) setTourOpen(true)
      }),
    [],
  )

  const chipState: ScanChipState = isFirstScan
    ? 'scanning'
    : isRescanning
      ? 'updating'
      : scanError
        ? 'failed'
        : hasRegions
          ? 'ready'
          : 'not_scanned'
  const chipStage = scanResult?.stage ?? regionsData?.stage ?? null

  return (
    <div data-testid="whitespace-tab">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <h2 className="text-sm font-semibold text-iflow-900 flex items-center gap-1.5">
          <Compass className="w-4 h-4" /> White Space Discovery
          <HelpPopover topic="ws-overview" size={14} />
        </h2>
        <div className="flex-1" />
        <div className="flex gap-0.5 bg-sky-500/10 rounded-lg p-0.5" role="tablist" aria-label="White space view">
          <button
            role="tab"
            aria-selected={view === 'explorer'}
            onClick={() => setView('explorer')}
            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
              view === 'explorer' ? 'bg-white text-iflow-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Opportunity Explorer
          </button>
          <button
            role="tab"
            aria-selected={view === 'matrix'}
            onClick={() => setView('matrix')}
            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
              view === 'matrix' ? 'bg-white text-iflow-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Combination Matrix
          </button>
          <button
            role="tab"
            aria-selected={view === 'map'}
            onClick={() => setView('map')}
            className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
              view === 'map' ? 'bg-white text-iflow-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <MapIcon className="w-3 h-3" /> Landscape Map
          </button>
          <button
            role="tab"
            aria-selected={view === '3d'}
            onClick={() => setView('3d')}
            className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
              view === '3d' ? 'bg-white text-iflow-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Box className="w-3 h-3" /> 3-D Explorer
          </button>
        </div>
        <ScanStatusChip state={chipState} stage={chipStage} completedAt={regionsData?.completed_at} />
        <button
          onClick={handleRegenerate}
          disabled={isScanRunning}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-sky-200 text-gray-700 hover:bg-sky-50 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isScanRunning ? 'animate-spin' : ''}`} />
          {isScanRunning ? 'Scanning…' : 'Regenerate'}
        </button>
      </div>

      <p className="text-[11px] text-gray-500 mb-3">{coverageNote}</p>

      {scanError && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg" role="alert">
          {scanError}
        </div>
      )}

      {notice && (
        <div className="mb-3 p-2 bg-sky-50 border border-sky-200 text-iflow-700 text-xs rounded-lg flex items-center justify-between">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} aria-label="Dismiss">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {isLoading && <LoadingSkeleton type="card" rows={3} />}

      {isError && !isLoading && (
        <div className="p-4 text-sm text-red-600" role="alert">
          Couldn't load white-space regions: {(error as Error)?.message ?? 'unknown error'}
        </div>
      )}

      {!isLoading && !isError && isFirstScan && (
        <div className="p-4 text-sm text-gray-600">Scanning the corpus for opportunities — this can take a few minutes.</div>
      )}

      {!isLoading && !isError && !isFirstScan && !hasRegions && (
        <EmptyState
          icon={<Compass className="w-7 h-7" />}
          title={firstRunCtaCopy(view).title}
          description={firstRunCtaCopy(view).description}
          action={{ label: 'Run scan', onClick: handleRegenerate }}
        />
      )}

      {!isLoading && !isError && hasRegions && view === 'explorer' && (
        <>
          <OpportunityExplorer
            regions={regions}
            onOpenDetail={(region) => setSelectedRegion(region)}
            onRunNoveltyProbe={handleRunNoveltyProbe}
            onToggleWatch={handleToggleWatch}
            onToggleWatchGroup={handleToggleWatchGroup}
            onRunAnalysisPanel={(region) => setPanelRegion(region)}
            watchedBandKeys={watchedBandKeys}
          />
          {selectedRegion && (
            <div className="mt-4" ref={detailRef}>
              <OpportunityDetail
                region={selectedRegion}
                isWatched={watchedBandKeys.has(regionBandKey(selectedRegion.band))}
                onToggleWatch={handleToggleWatch}
                onRunNoveltyProbe={handleRunNoveltyProbe}
                onClose={() => setSelectedRegion(null)}
              />
            </div>
          )}
          {panelRegion && (
            <div className="mt-4">
              <AnalysisPanel
                projectId={projectId}
                recordIds={regionWatchRecordIds(panelRegion, MAX_PANEL_RECORD_IDS)}
                title={regionLabel(panelRegion)}
                onClose={() => setPanelRegion(null)}
              />
            </div>
          )}
        </>
      )}

      {!isLoading && !isError && hasRegions && view === 'matrix' && (
        isMatrixLoading ? (
          <LoadingSkeleton type="card" rows={3} />
        ) : (
          <CombinationMatrix
            matrix={matrix}
            projectId={projectId}
            onExploreAsOpportunity={() => setNotice('Turning a matrix cell directly into an opportunity isn\'t available yet.')}
            onRunNoveltyProbe={handleRunNoveltyProbe}
            onDecomposed={() => refetchMatrix()}
          />
        )
      )}

      {!isLoading && !isError && hasRegions && view === 'map' && (
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
          <div>
            {mapError && (
              <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg" role="alert">
                {mapError}
              </div>
            )}
            {isMapLoading ? (
              <LoadingSkeleton type="card" rows={3} />
            ) : (
              <LandscapeMap
                layout={mapData?.layout ?? {}}
                dates={mapData?.dates}
                regions={regions}
                probeLandings={probeLandings}
                onGenerateMap={handleGenerateMap}
                isGenerating={isBuildingMap}
                onRegionClick={(regionKey) => {
                  const match = regions.find((r) => regionKeyFor(r) === regionKey)
                  if (match) {
                    setSelectedRegion(match)
                    setView('explorer')
                  }
                }}
                onProbeRegion={(region) => {
                  setProbeContext(regionLabel(region))
                  setProbeContextNonce((n) => n + 1)
                }}
              />
            )}
          </div>
          <ProbePanel
            key={probeContextNonce}
            projectId={projectId}
            initialContext={probeContext}
            onLandings={setProbeLandings}
            onRunNoveltyAnalysis={handleRunNoveltyProbe}
          />
        </div>
      )}

      {!isLoading && !isError && hasRegions && view === '3d' && (
        <div>
          {map3DError && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg" role="alert">
              {map3DError}
            </div>
          )}
          {isMap3DLoading ? (
            <LoadingSkeleton type="card" rows={3} />
          ) : (
            <Suspense fallback={<LoadingSkeleton type="card" rows={3} />}>
              <Explorer3D
                layout={map3DData?.layout ?? null}
                dates={map3DData?.dates}
                onGenerate3D={handleGenerate3D}
                isGenerating={isBuilding3D}
                buildStartedAt={build3DStartedAt}
                regions={regions}
                competitorRegions={competitorsData?.regions}
                defaultAssignment={DEFAULT_CHANNEL_ASSIGNMENT}
                onRefetch3D={refetch3D}
              />
            </Suspense>
          )}
        </div>
      )}

      <GuidedTour
        tourId={READING_SCAN_TOUR_ID}
        steps={scanTourSteps}
        open={tourOpen}
        onClose={() => setTourOpen(false)}
      />
    </div>
  )
}
